'use client';

import FlightUpIcon from '@/assets/icon/flight-up';
import { Briefcase, Clock, Luggage } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Root, LayoverStopsData } from './list/flightlist/types';
import AccordionTab from '@/components/accordionTab';
import Image from '@/lib/app-image';
import FlightDownIcon from '@/assets/icon/flight-down';
import { useFareRuleMutation } from '@flysync/shared-redux/services/booking';
import { useSelector } from 'react-redux';
import defaultLogo from '@/assets/airlines/none.png'; // Import default image
import { getAirlineIconUrl } from '@/lib/airlineIcon';
import { format, parseISO } from 'date-fns';
import {
  FormattedFareRuleData,
  formatFetchFareRuleResponse,
} from '@/lib/fare-rule-formatter';

interface FlightDetailI {
  data: Root;
  type: 'OUT_BOUND' | 'RETURN';
}

function Flights({ data, type }: FlightDetailI) {
  const [getFareRule, { isLoading: fareRuleLoader }] = useFareRuleMutation();
  const [fareRules, setFareRules] = useState<FormattedFareRuleData | null>(null);
  const stateData = useSelector((state: any) => state.flightSearch);
  const [isApi, setIsApi] = useState(false);

  const fetchFareRules = async () => {
    const fareRuleD = {
      airline_logo: stateData?.data[0]?.airline_logo,
      airline_code: stateData?.data[0]?.airline_code,
      airline_name: stateData?.data[0]?.airline_name,
      origin: stateData?.data[0]?.origin,
      destination: stateData?.data[0]?.destination,
      flight_number: stateData?.data[0]?.flight_number,
      departure_date: stateData?.data[0]?.departure_date,
      arrival_date: stateData?.data[0]?.arrival_date,
      departure_time: stateData?.data[0]?.departure_time,
      arrival_time: stateData?.data[0]?.arrival_time,
      departure_terminal: stateData?.data[0]?.departure_terminal,
      arrival_treminal: stateData?.data[0]?.arrival_treminal,
      departure_airport_name:
        stateData?.data[0]?.departure_astateDatairport_name,
      arrival_airport_name: stateData?.data[0]?.arrival_airport_name,
      departure_city_name: stateData?.data[0]?.departure_city_name,
      arrival_city_name: stateData?.data[0]?.arrival_city_name,
      departure_country_name: stateData?.data[0]?.departure_country_name,
      arrival_country_name: stateData?.data[0]?.arrival_country_name,
      duration_stops: stateData?.data[0]?.duration_stops,
      stop_count: stateData?.data[0]?.stop_count,
      travel_time: stateData?.data[0]?.travel_time,
      arrival_terminal: stateData?.data[0]?.arrival_terminal,
      travel_date: stateData?.data[0]?.travel_date,
      origin_airport: stateData?.data[0]?.origin_airport,
      destination_airport: stateData?.data[0]?.destination_airport,
      supplier: stateData?.data[0]?.supplier,
      travel_type: stateData?.data[0]?.travel_type,
      trip_type: stateData?.data[0]?.trip_type,
      cabin_type: stateData?.data[0]?.cabin_type,
      fareTypeId: stateData?.data[0]?.fareTypeId,
      refundType: stateData?.data[0]?.refundType,
      departureDate: stateData?.data[0]?.departureDate,
      bookingClass: stateData?.data[0]?.bookingClass,
      fareBasis: stateData?.data[0]?.fareBasis,
      price_details: stateData?.data[0]?.price_details,
    };
    try {
      const response = await getFareRule({
        data: {
          flowType: 'review',
          data: [fareRuleD],
        },
      }).unwrap();
      setFareRules(formatFetchFareRuleResponse(response));
    } catch (error) {
      console.error('Error fetching fare rules:', error);
    }
  };

  useEffect(() => {
    if (!isApi && stateData?.data[0]?.airline_code) {
      setIsApi(true);
      fetchFareRules();
    }
  }, [stateData?.data[0], stateData?.data[0]?.airline_code]);
  return (
    <div className="">
      {/* <span className="flex w-full items-center gap-1.5 text-sm">
        {type === 'OUT_BOUND' ? (
          <FlightUpIcon className="h-7 w-7" />
        ) : (
          <FlightDownIcon className="h-7 w-7" />
        )}{' '}
        {type === 'OUT_BOUND' ? 'Departing Flight' : 'Return Flight'} •{' '}
        {data?.departure_date}
      </span> */}
      <div className="flex w-full flex-col items-center gap-2">
        {data?.flightInfo?.map((flight, index) => (
          <div className="flex w-full flex-col" key={index}>
            <span className="flex w-full items-center gap-1.5 text-xs md:text-sm">
              {type === 'OUT_BOUND' && index < 1 ? (
                <FlightUpIcon className="h-5 w-5 md:h-7 md:w-7" />
              ) : (
                <FlightDownIcon className="h-5 w-5 md:h-7 md:w-7" />
              )}{' '}
              {type === 'OUT_BOUND' && index < 1
                ? 'Departing Flight'
                : 'Return Flight'}{' '}
              •{' '}
              {type === 'OUT_BOUND' && index < 1
                ? format(flight?.departure_date, 'eee, d MMMM yyyy')
                : format(flight?.arrival_date, 'eee, d MMMM yyyy')}
            </span>
            <div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-2">
              <h3 className="text-lg font-semibold text-zinc-800 md:text-2xl">{`${flight?.departure_city_name} - ${flight?.arrival_city_name}`}</h3>
              <span className="text-xs text-zinc-800">
                {flight?.stops} • {flight?.duration}
              </span>
            </div>

            {data?.layoverStopsData?.map(
              (layover: LayoverStopsData, layoverIndex: number) =>
                layover?.tripIndex === index && (
                  <React.Fragment key={layoverIndex}>
                    <StopItem
                      layover={layover}
                      priceDetails={data?.price_details}
                    />
                    {data?.layover?.map((layoverStops, i) => (
                      <React.Fragment key={i}>
                        {layoverIndex < data.layoverStopsData.length - 1 &&
                          layoverStops?.city === layover?.arrival_city_name &&
                          layover?.destination !== data?.destination &&
                          index === layoverStops?.tripIndex && (
                            <div className="my-3 flex w-full items-center gap-2 md:my-4">
                              <div className="h-[1px] w-full border-t border-dashed border-text_secondary"></div>
                              <div className="whitespace-nowrap rounded-full bg-light px-4 py-1 text-[10px] text-zinc-800 md:px-32 md:text-xs">
                                {layoverStops?.layoverTime &&
                                !layoverStops?.layoverTime.includes('NaN')
                                  ? layoverStops?.layoverTime
                                  : '00'}{' '}
                                Layover in {layoverStops?.city} (Plane Change)
                              </div>
                              <div className="h-[2px] w-full border-t border-dashed border-text_secondary"></div>
                            </div>
                          )}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ),
            )}
            <div className="mb-3 md:mb-4">
              <AccordionTab
                data={data}
                fareRules={fareRules}
                isLoader={fareRuleLoader}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Flights;

const StopItem = ({
  layover,
  priceDetails,
}: {
  layover: LayoverStopsData;
  priceDetails: any;
}) => {
  const [imgError, setImgError] = useState(false);

  return (
    <>
      <div className="my-3 flex items-center gap-2 md:my-5">
        {layover?.airline_name && (
          <Image
            alt="airline_logo"
            crossOrigin="anonymous"
            src={
              imgError
                ? defaultLogo
                : getAirlineIconUrl(
                    layover?.airline_logo || layover?.airline_code,
                  )
            }
            onError={() => setImgError(true)}
            className="h-6 w-6 md:h-8 md:w-8"
          />
        )}
        <div className="flex items-center gap-2">
          <h4 className="text-sm font-medium text-zinc-800 md:text-base">
            {layover?.airline_name}
          </h4>
          <span className="text-xs md:text-sm">{`${layover?.flight_number}`}</span>
        </div>
      </div>
      <div className="flex">
        <div className="flex flex-col items-center justify-between py-2">
          <div className="h-2 w-2 rounded-full border border-gray-300 bg-white"></div>
          <div className="border-text-secondary h-5/6 w-1 border-l-2 border-dashed"></div>
          <div className="h-2 w-2 rounded-full border border-gray-300 bg-white"></div>
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2">
            <h3 className="ml-4 text-xs font-semibold text-zinc-800 md:text-sm">
              {layover?.departure_city_name} ({layover?.origin}){' '}
              {layover?.departure_time}
            </h3>
            <div className="ml-4 text-xs text-zinc-800 md:ml-0 md:text-sm">
              {layover?.departure_airport_name} {layover?.departure_city_name}{' '}
              {layover?.departure_terminal || layover?.arrival_treminal}
            </div>
          </div>
          <div className="my-3 ml-4 flex items-center gap-2 md:my-4">
            <Clock className="h-3 w-3 text-orange-500 md:h-4 md:w-4" />
            <span className="text-xs text-zinc-800 md:text-sm">
              {layover?.duration}
            </span>
          </div>
          <div className="flex flex-col gap-1 md:flex-row md:items-center md:gap-2">
            <h3 className="ml-4 text-xs font-semibold text-zinc-800 md:text-sm">
              {layover?.arrival_city_name} ({layover?.destination}){' '}
              {layover?.arrival_time}
            </h3>
            <div className="ml-4 text-xs text-zinc-800 md:ml-0 md:text-sm">
              {layover?.arrival_airport_name} {layover?.arrival_city_name}{' '}
              {layover?.arrival_terminal || layover?.arrival_treminal}
            </div>
          </div>
        </div>
      </div>
      <div className="my-3 flex flex-col gap-3 rounded-xl border border-background px-3 py-2 md:my-5 md:gap-6 md:px-5">
        <div className="flex flex-col gap-3 md:flex-row md:gap-4">
          <div className="rounded-lg bg-background px-4 py-2 text-xs md:px-7">
            {`${layover?.origin} - ${layover?.destination}`}
          </div>
          <div className="flex flex-col gap-2 text-xs text-zinc-800 md:flex-row md:items-center md:gap-4">
            <span className="flex items-center gap-1 text-xs text-zinc-800">
              <Luggage className="h-4 w-4 md:h-5 md:w-5" /> Check-in Baggage:{' '}
              {priceDetails?.[0]?.checkin_baggage}
            </span>
            <span className="flex items-center gap-1 text-xs text-zinc-800">
              <Briefcase className="h-3 w-3 md:h-4 md:w-4" /> Cabin Baggage:{' '}
              {priceDetails?.[0]?.hand_baggage}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
